/* eslint-disable jsx-a11y/label-has-associated-control */
import { h } from 'preact'
import { Geografi, isGeografi, isParti, Parti } from '../../../domain'
import { createUrlFor } from '../../../routes'

import { useSearch } from './search-hook'
import { Message, message } from '../../../../lib/intl/Message'
import { useLocale, useLocalizedValue } from '../../../../lib/intl/IntlProvider'

import styles from './Search.css'
import { Icon } from '../../shared/Icons/Icons'
import { useCustomContext } from '../../../../lib/context'
import { GA_EVENTS } from '../../../../lib/analytics'

const CoreSuggest = props => h('core-suggest', props)

type Props = {
  geografi: Geografi[]
  partier: Parti[]
}

export function Search(props: Props) {
  const { history, sendGaEvent } = useCustomContext()
  const { partier, geografi } = props
  const { ref, query, results, clear } = useSearch(geografi, partier)
  const locale = useLocale()
  const localize = useLocalizedValue()

  const onKeyDown = e => {
    if (e.key === 'Enter') {
      const results = ref.current
        ? (ref.current.querySelectorAll('li a') as NodeListOf<HTMLAnchorElement>)
        : []
      if (results.length === 0) {
        e.preventDefault()
      } else if (results.length === 1) {
        results[0].click()
      } else {
        e.preventDefault()
        results[0].focus()
      }
    }
  }

  function handleClick(event: MouseEvent) {
    clear()
    const target = event.currentTarget

    if (history && target instanceof HTMLAnchorElement) {
      sendGaEvent(GA_EVENTS.LINK_CLICK('SEARCH', target.pathname))

      event.preventDefault()

      history.push({
        pathname: target.pathname,
        search: target.search
      })
    }
  }

  function handleClearClick() {
    clear()
  }

  /**
   * Logs search value changes after an amount of time has passed since the last time the user typed.
   * The purpose is to prevent logging 'D', then 'Do', then 'Dov', then 'Dovr' and finally 'Dovre'.
   */

  return (
    <div class={styles.search}>
      <div ref={ref} class={styles.search__container}>
        <label for="sok" class="nrk-sr">
          <Message id="sok-label" />
        </label>
        <input
          id="sok"
          type="text"
          spellcheck={false}
          required
          onKeyDown={onKeyDown}
          placeholder={message('sok-placeholder')}
        />
        <CoreSuggest hidden>
          <ul key={query}>
            {results.map((res, index) => {
              return (
                <li>
                  <a
                    href={createUrlFor(res, locale)}
                    tabIndex={-1}
                    type="button"
                    aria-label={`${localize(res.navn)}, ${index + 1} av ${results.length}`}
                    onClick={handleClick}
                  >
                    {isGeografi(res) && res.nyttNavn
                      ? localize(res.navn) + ' → ' + localize(res.nyttNavn)
                      : isParti(res) && res.sokbartKortNavn
                      ? localize(res.navn) + ' (' + res.sokbartKortNavn + ')'
                      : localize(res.navn)}
                  </a>
                </li>
              )
            })}
          </ul>
        </CoreSuggest>
        <Icon type="nrk-search" class={styles.search__icon} />

        <button
          type="button"
          class={styles.search__reset}
          aria-label="Nullstill"
          onClick={handleClearClick}
        >
          <Icon class={styles.search__icon} type="nrk-close" />
        </button>
      </div>
    </div>
  )
}
